<script setup>
import { defineVaDataTableColumns, VaBadge, VaDataTable, VaPagination } from 'vuestic-ui';
import VaChart from '../va-charts/VaChart.vue';
import { computed, ref } from 'vue';
import { formatMoney } from '../../services/utils';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  companies: {
    type: Array,
    default: () => [],
  },
  startDate: {
    type: String,
    default: null,
  },
  endDate: {
    type: String,
    default: null,
  },
})

const reloadChart = ref(false);
const currentPage = ref(1);
const perPage = ref(20);

const pages = computed(() => {
  return Math.ceil(props.data.length / perPage.value);
});

const columns = defineVaDataTableColumns([
  { label: 'Ação', key: 'action' },
  { label: 'Profissional', key: 'entity.professional.name' },
  { label: 'Cliente', key: 'entity.customer.name' },
  { label: 'Método de Pagamento', key: 'entity.payment_method' },
  { label: 'Status', key: 'entity.status' },
  { label: 'Valor Total', key: 'entity.total' },
  { label: 'Data', key: 'entity.created_at', displayFormatFn: (value) => new Date(value).toLocaleDateString() },
])

const monthDays = computed(() => {
  const daysInMonth = [];
  const start = props.startDate ? new Date(props.startDate + 'T00:00:00') : null;
  const end = props.endDate ? new Date(props.endDate + 'T00:00:00') : null;

  if (start && end) {
    if (start.getMonth() !== end.getMonth() || start.getFullYear() !== end.getFullYear()) {
      return daysInMonth;
    }

    const current = new Date(start);
    while (current <= end) {
      daysInMonth.push(new Date(current).getDate().toString().padStart(2, '0'));
      current.setDate(current.getDate() + 1);
    }
  }

  return daysInMonth;
});

const yearMonths = computed(() => {
  const monthsInYear = [];
  const start = props.startDate ? new Date(props.startDate + 'T00:00:00') : null;
  const end = props.endDate ? new Date(props.endDate + 'T00:00:00') : null;

  if (start && end) {
    if (start.getMonth() === end.getMonth()) {
      return monthsInYear;
    }

    for (let month = start.getMonth(); month <= end.getMonth(); month++) {
      monthsInYear.push((month + 1).toString().padStart(2, '0'));
    }
  }

  return monthsInYear;
});

const labels = computed(() => {
  return monthDays.value.length ? monthDays.value : yearMonths.value;
});

const createdDataValues = computed(() => {
  const data = [];
  labels.value.forEach(label => {
    const count = props.data.filter(log => {
      if (log.action !== 'created') return false;

      const logDate = new Date(log.created_at);
      const logDay = logDate.getDate().toString().padStart(2, '0');
      const logMonth = (logDate.getMonth() + 1).toString().padStart(2, '0');

      if (monthDays.value.length) {
        return logDay === label;
      } else if (yearMonths.value.length) {
        return logMonth === label;
      }
      return false;
    }).length;
    data.push(count);
  });
  return data;
});

const createdDataset = computed(() => [
  {
    label: 'Pedidos Criados',
    backgroundColor: 'rgba(54, 162, 235, 0.6)',
    data: createdDataValues.value,
  },
]);

const totalCreatedUsers = computed(() => {
  return createdDataValues.value.reduce((a, b) => a + b, 0);
});

const options = {
  scales: {
    x: {
      display: false,
      grid: {
        display: false,
      },
    },
    y: {
      display: false,
      grid: {
        display: false,
      },
      ticks: {
        display: false,
      },
    },
  },
  interaction: {
    intersect: false,
    mode: 'index',
  },
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      enabled: true,
    },
  },
}
</script>

<template>
  <div class="space-y-8">
    <!-- <pre>
      {{ data }}
    </pre> -->
    <div>
      <div class="mt-6 grid">
        <div>
          <h3 class="text-lg font-bold">Pedidos no período ({{ totalCreatedUsers }})</h3>
          <div>
            <VaChart v-if="!reloadChart" :data="{
              labels: labels,
              datasets: createdDataset
            }" class="h-48" type="line" :options="options" />
          </div>
        </div>
      </div>
    </div>

    <VaDataTable :wrapper-size="400" :item-size="40" virtual-scroller sticky-header :per-page="perPage"
      :current-page="currentPage" hoverable :items="data" :columns="columns">
      <template #cell(action)="{ rowData }">
        <div class="flex items-center gap-2">
          <VaBadge v-if="rowData.action == 'created'" text="Criado" color="success" />
          <VaBadge v-if="rowData.action == 'deleted'" text="Removido" color="danger" />
        </div>
      </template>

      <template #cell(entity.payment_method)="{ rowData }">
        <div class="flex items-center gap-2">
          <VaBadge v-if="rowData.entity.payment_method == 'credit_card'" text="Cartão de Crédito" color="primary" />
        </div>
      </template>

      <template #cell(entity.status)="{ rowData }">
        <div class="flex items-center gap-2">
          <VaBadge v-if="rowData.entity.status == 'paid'" text="Pago" color="success" />
          <VaBadge v-if="rowData.entity.status == 'late'" text="Atrasado" color="danger" />
          <VaBadge v-if="rowData.entity.status == 'pending'" text="Pendente" color="warning" />
          <VaBadge v-if="rowData.entity.status == 'processing'" text="Processando" color="info" />
          <VaBadge v-if="rowData.entity.status == 'refund'" text="Reembolsado" color="secondary" />
          <VaBadge v-if="rowData.entity.status == 'refund_lost'" text="Reembolso Perdido" color="danger" />
          <VaBadge v-if="rowData.entity.status == 'refund_win'" text="Reembolso Ganhou" color="success" />
        </div>
      </template>

      <template #cell(entity.total)="{ rowData }">
        <div class="flex items-center gap-2">
          <span class="font-bold text-success">
            {{ formatMoney(rowData.entity.total) }}
          </span>
        </div>
      </template>

      <template #bodyAppend>
        <tr>
          <td colspan="6">
            <div class="flex justify-center mt-4">
              <VaPagination v-model="currentPage" :pages="pages" />
            </div>
          </td>
        </tr>
      </template>
    </VaDataTable>
  </div>
</template>

<style scoped></style>
